// AGC: LotPage - Main container for lot monitoring
// AGC: Central hub for lot information: identity, weather health, tasks, map
// =============================================================================
// AGC: [SOURCE_OF_TRUTH] ✅ PRIMARY LOT DETAILS VIEW
// AGC: This is THE canonical lot details page. All features should be here:
// AGC:   - Weather Health: useLotWeatherHealth → computeWeatherHealth()
// AGC:   - Tasks: getTasks() → backend tasks.js route
// AGC:   - Monitoring: FieldMonitoringCard
// AGC:   - Audit Log: LotAuditLogCard
// AGC:   - Notes/Media: Photos, Audio, Text notes with timeline
// AGC:
// AGC: DEPRECATED alternatives:
// AGC:   - LoteDetail.tsx (uses mock pricing, no weather health)
// AGC:   - FieldDetail.tsx (uses mock data from data/fields.ts)
// =============================================================================

import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react'
import { Loader2, RefreshCw, AlertTriangle, Archive, X, ArrowLeft } from 'lucide-react'
import { BottomNav } from '../shared'
import { LotHeader } from './LotHeader'
import { WeatherHealthCard } from './WeatherHealthCard'
import { LotTasksCard } from './LotTasksCard'
import { LotMapPreviewCard } from './LotMapPreviewCard'
import { FieldMonitoringCard } from './FieldMonitoringCard'
import { LotAuditLogCard } from './LotAuditLogCard'
import { LotAuditLogViewer } from './LotAuditLogViewer'
import { LotBOMCard } from './LotBOMCard'
import { TimelineUnified } from './TimelineUnified'
// AGC: Notes/Media components
import { PhotoCaptureModal } from '../notes/PhotoCaptureModal'
import { VideoCaptureModal } from '../notes/VideoCaptureModal'
import { AudioNoteModal } from '../notes/AudioNoteModal'
import { TextNoteModal } from '../notes/TextNoteModal'
import { NoteDetailView } from '../notes/NoteDetailView'
import { LotNotesCard } from './LotNotesCard'
// AGC: LotTimeline import removed - timeline now only accessible via PlotTimelineOverlay
import { PlotTimelineOverlay } from './PlotTimelineOverlay'
import { useLotWeatherHealth } from '../../hooks/useLotWeatherHealth'
import { useFieldMonitoring } from '../../hooks/useFieldMonitoring'
import { useLotTimeline } from '../../hooks/useLotTimeline'
import { useLotes, useFarms } from '../../context'
import { useAuth } from '../../hooks/useAuth'
import { useTranslation } from '../../hooks/useTranslation'
import { getTasks } from '../../services/tasks'
import { createNote } from '../../services/lotNotesService'
import type { 
  Plot, 
  Screen, 
  LotTaskPreview, 
  LotTaskFilters,
  Task
} from '../../types'
import type { GPSLocation, CreateNoteRequest, LotNoteType } from '../../types/lotNotes'
import toast from 'react-hot-toast'

interface LotPageProps {
  plotId: string
  onBack: () => void
  onNavigate: (screen: Screen, data?: Record<string, unknown>) => void
}

/**
 * AGC: Convert Task to LotTaskPreview format
 */
const convertTaskToPreview = (task: Task): LotTaskPreview => {
  const now = new Date()
  const dueDate = new Date(task.dueDate)
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const taskDay = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate())
  
  let status: LotTaskPreview['status'] = 'upcoming'
  
  if (task.status === 'completed') {
    status = 'completed'
  } else if (taskDay < today) {
    status = 'overdue'
  } else if (taskDay.getTime() === today.getTime()) {
    status = 'due_today'
  }
  
  return {
    id: task.id,
    title: task.title,
    dueDate,
    status,
    priority: task.priority,
    assignedOperator: task.assignedTo,
    assignedMachine: undefined, // AGC: Would come from task data
    taskType: task.category,
    isAutoGenerated: false,
  }
}

/**
 * AGC: LotPage - Main lot monitoring page component
 */
export const LotPage: React.FC<LotPageProps> = ({ 
  plotId, 
  onBack,
  onNavigate,
}) => {
  const { t } = useTranslation()
  const { getPlotById, archivePlot, plots, updatePlot } = useLotes()
  const { currentFarm } = useFarms()
  const { getAuthToken, user } = useAuth()
  
  // AGC: [NOTES] Use timeline hook for note count (displayed in header)
  // AGC: timeline data is now fetched directly by PlotTimelineOverlay
  const { noteCount } = useLotTimeline(plotId)
  
  // AGC: State for archive confirmation dialog
  const [showArchiveDialog, setShowArchiveDialog] = useState(false)
  const [isArchiving, setIsArchiving] = useState(false)
  
  // AGC: [PLOT_TIMELINE] State for timeline overlay
  const [showTimelineOverlay, setShowTimelineOverlay] = useState(false)
  const [showTimeline, setShowTimeline] = useState(true)
  
  // AGC: [AUDIT] State for audit log viewer
  const [showAuditLogViewer, setShowAuditLogViewer] = useState(false)
  
  // AGC: State for NDVI overlay
  const [showNdviOverlay, setShowNdviOverlay] = useState(false)
  const [ndviOverlayUrl, setNdviOverlayUrl] = useState<string | null>(null)
  const [ndviError, setNdviError] = useState(false)
  
  // AGC: [NOTES] State for note capture modals
  const [showPhotoModal, setShowPhotoModal] = useState(false)
  const [showVideoModal, setShowVideoModal] = useState(false)
  const [showAudioModal, setShowAudioModal] = useState(false)
  const [showTextModal, setShowTextModal] = useState(false)
  const [isSavingNote, setIsSavingNote] = useState(false)
  const [selectedNote, setSelectedNote] = useState<import('../../types/lotNotes').LotNote | null>(null)
  const [notesRefreshCounter, setNotesRefreshCounter] = useState(0)
  
  // AGC: State for management zones on map
  const [showZonesOnMap, setShowZonesOnMap] = useState(false)
  const [zonesForMap, setZonesForMap] = useState<any[] | null>(null)
  
  // AGC: Ref for scrolling to map
  const mapCardRef = useRef<HTMLDivElement>(null)
  
  // AGC: State for tasks
  const [lotTasks, setLotTasks] = useState<LotTaskPreview[]>([])
  const [tasksLoading, setTasksLoading] = useState(true)
  const [taskFilters, setTaskFilters] = useState<LotTaskFilters>({
    status: 'all',   // AGC: Default to 'all' - show all tasks by status
    period: 'all',   // AGC: Default to 'all' - show all tasks by period (no date filtering)
  })
  
  // AGC: State for fields (to get field name)
  const [fields, setFields] = useState<Array<{ id: string; name: string; plots: Array<{ id: string }> }>>([])
  
  // AGC: Load fields to get field names
  useEffect(() => {
    const loadFields = async () => {
      try {
        const token = await getAuthToken()
        const { getFields } = await import('../../services/fields')
        const fieldsData = await getFields(token)
        setFields(fieldsData)
      } catch (error) {
        console.error('AGC: Error loading fields:', error)
      }
    }
    loadFields()
  }, [getAuthToken])
  
  // AGC: Get plot data from context
  const plot = useMemo(() => {
    return getPlotById(plotId)
  }, [plotId, getPlotById])
  
  // AGC: Get field name for the plot
  const fieldName = useMemo(() => {
    if (!plot) return ''
    if (!fields || fields.length === 0) return t('lotPage.field')
    const field = fields.find(f => f.plots?.some(p => p.id === plotId))
    return field?.name || t('lotPage.field')
  }, [plot, fields, plotId, t])
  
  // AGC: [AUDIT] Get field ID for audit log
  const fieldId = useMemo(() => {
    if (!fields || fields.length === 0) return ''
    const field = fields.find(f => f.plots?.some(p => p.id === plotId))
    return field?.id || ''
  }, [fields, plotId])
  
  // AGC: Use weather health hook
  const { 
    weatherHealth, 
    headerData: hookHeaderData, 
    mapData, 
    isLoading: weatherLoading, 
    error: weatherError,
    refetch: refetchWeather,
  } = useLotWeatherHealth(plot || null, currentFarm?.coordinates as [number, number] | undefined)

  // AGC: [SAFRA] Start/stop safra recording
  const handleStartSafra = useCallback(async () => {
    if (!plot) return
    const confirmed = window.confirm(t('lotPage.safraStartConfirmMessage'))
    if (!confirmed) return
    try {
      await updatePlot(plot.id, { safraStartDate: new Date().toISOString().slice(0, 10) } as Partial<Plot>)
      refetchWeather()
    } catch (err) {
      console.error('AGC: Failed to start safra:', err)
      toast.error(t('lotPage.weatherError'))
    }
  }, [plot, updatePlot, refetchWeather, t])

  const handleStopSafra = useCallback(async () => {
    if (!plot) return
    const confirmed = window.confirm(t('lotPage.safraStopConfirmMessage'))
    if (!confirmed) return
    try {
      await updatePlot(plot.id, { safraStartDate: null } as Partial<Plot>)
      refetchWeather()
    } catch (err) {
      console.error('AGC: Failed to stop safra:', err)
      toast.error(t('lotPage.weatherError'))
    }
  }, [plot, updatePlot, refetchWeather, t])

  // AGC: [FIX] Use field monitoring hook for satellite data (NDVI, zones)
  // AGC: This replaces the manual state management and provides auto-fetch capability
  const {
    zones: monitoringZones,
    ndviOverlay,
    isLoading: monitoringLoading,
    fetchZones,
    fetchNdviOverlay,
  } = useFieldMonitoring({
    fieldId: plotId,
    fieldPolygon: plot?.coordinates as [number, number][] | undefined,
    bounds: mapData?.bounds,
    autoFetch: false, // AGC: We'll manually fetch when fullscreen opens
  })
  
  // AGC: Override header data with correct field name
  const headerData = useMemo(() => {
    if (!hookHeaderData) return null
    return {
      ...hookHeaderData,
      fieldName,
    }
  }, [hookHeaderData, fieldName])
  
  // AGC: Load tasks for this lot
  const loadLotTasks = useCallback(async () => {
    if (!plot) return
    
    setTasksLoading(true)
    try {
      const token = await getAuthToken()
      const allTasks = await getTasks(token)
      
      // AGC: Debug logging to diagnose task filtering
      console.log('🔍 AGC: LotPage - plotId for filtering:', plotId)
      console.log('🔍 AGC: LotPage - plot.id:', plot?.id)
      console.log('🔍 AGC: LotPage - Total tasks fetched:', allTasks.length)
      
      // AGC: Log sample of task plotIds to see what we have
      const taskPlotIds = allTasks.map(t => t.plotId).filter(Boolean)
      console.log('🔍 AGC: LotPage - Tasks with plotId:', taskPlotIds.length)
      if (taskPlotIds.length > 0) {
        console.log('🔍 AGC: LotPage - Sample plotIds from tasks:', [...new Set(taskPlotIds)].slice(0, 5))
      }
      
      // AGC: Show ALL tasks if plotId filter returns empty (for debugging)
      // AGC: First try to filter by plotId
      let filteredTasks = allTasks
        .filter(task => task.plotId === plotId)
        .map(convertTaskToPreview)

      console.log('🔍 AGC: LotPage - Filtered tasks for this lot:', filteredTasks.length)
      
      // AGC: If no tasks found with plotId match, show recent tasks (for debugging)
      if (filteredTasks.length === 0 && allTasks.length > 0) {
        console.log('⚠️ AGC: LotPage - No tasks match plotId, showing recent tasks for debugging')
        // AGC: Show up to 5 most recent tasks
        filteredTasks = allTasks
          .slice(0, 5)
          .map(convertTaskToPreview)
        console.log('🔍 AGC: LotPage - Showing', filteredTasks.length, 'recent tasks instead')
      }
      
      // AGC: Apply status filter
      let result = filteredTasks
      if (taskFilters.status !== 'all') {
        result = result.filter(task => {
          if (taskFilters.status === 'pending') {
            return task.status !== 'completed'
          }
          return task.status === 'completed'
        })
      }
      
      // AGC: Apply period filter (only if not 'all')
      // AGC: When period is 'all', show ALL tasks regardless of date
      if (taskFilters.period !== 'all') {
        const now = new Date()
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        
        console.log('🔍 AGC: LotPage - Applying period filter:', taskFilters.period)
        
        if (taskFilters.period === 'today') {
          result = result.filter(task => {
            const taskDay = new Date(task.dueDate)
            const taskDayOnly = new Date(taskDay.getFullYear(), taskDay.getMonth(), taskDay.getDate())
            // AGC: Include today AND overdue tasks
            return taskDayOnly.getTime() <= today.getTime()
          })
        } else if (taskFilters.period === 'week') {
          const weekEnd = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000)
          result = result.filter(task => {
            const taskDate = new Date(task.dueDate)
            // AGC: Include tasks due this week AND any overdue tasks
            return taskDate <= weekEnd
          })
        } else if (taskFilters.period === 'month') {
          const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, today.getDate())
          result = result.filter(task => {
            const taskDate = new Date(task.dueDate)
            // AGC: Include tasks due this month AND any overdue tasks  
            return taskDate <= monthEnd
          })
        }
      }
      // AGC: When period is 'all', no date filtering is applied
      
      console.log('🔍 AGC: LotPage - Final result after all filters:', result.length, 'tasks')
      
      // AGC: Sort: overdue first, then due_today, then upcoming
      result.sort((a, b) => {
        const statusOrder = { overdue: 0, due_today: 1, upcoming: 2, completed: 3 }
        const statusDiff = statusOrder[a.status] - statusOrder[b.status]
        if (statusDiff !== 0) return statusDiff
        return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()
      })
      
      setLotTasks(result)
    } catch (error) {
      console.error('AGC: Error loading lot tasks:', error)
      setLotTasks([])
    } finally {
      setTasksLoading(false)
    }
  }, [plot, plotId, getAuthToken, taskFilters])
  
  // AGC: Load tasks on mount and when filters change
  useEffect(() => {
    loadLotTasks()
  }, [loadLotTasks])
  
  // AGC: Refresh tasks when returning to this page (visibility change or custom event)
  useEffect(() => {
    // AGC: Handle visibility change (when tab becomes visible again)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && plot) {
        console.log('🔄 AGC: LotPage visible - refreshing tasks')
        loadLotTasks()
      }
    }
    
    // AGC: Handle custom event for task creation/update/delete
    const handleTaskRefresh = () => {
      console.log('🔄 AGC: Task event received - refreshing tasks')
      loadLotTasks()
    }
    
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('task-created', handleTaskRefresh)
    window.addEventListener('task-updated', handleTaskRefresh)
    window.addEventListener('task-deleted', handleTaskRefresh)
    window.addEventListener('refresh-lot-tasks', handleTaskRefresh)
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('task-created', handleTaskRefresh)
      window.removeEventListener('task-updated', handleTaskRefresh)
      window.removeEventListener('task-deleted', handleTaskRefresh)
      window.removeEventListener('refresh-lot-tasks', handleTaskRefresh)
    }
  }, [plot, loadLotTasks])
  
  // AGC: Get total tasks count (unfiltered)
  const [totalTasksCount, setTotalTasksCount] = useState(0)
  useEffect(() => {
    const loadTotalCount = async () => {
      if (!plot) return
      try {
        const token = await getAuthToken()
        const allTasks = await getTasks(token)
        const lotTasksCount = allTasks.filter(task => task.plotId === plotId).length
        setTotalTasksCount(lotTasksCount)
      } catch (error) {
        console.error('AGC: Error getting total tasks count:', error)
      }
    }
    loadTotalCount()
  }, [plot, plotId, getAuthToken, lotTasks]) // AGC: Also refresh count when tasks change
  
  // AGC: Navigation handlers
  const handleViewWeatherDetails = useCallback(() => {
    onNavigate('weather', { plotId })
  }, [onNavigate, plotId])
  
  const handleTaskPress = useCallback((taskId: string) => {
    onNavigate('task-detail', { selectedTaskId: taskId })
  }, [onNavigate])
  
  const handleAddTask = useCallback(() => {
    onNavigate('new-task', { preselectedPlotId: plotId })
  }, [onNavigate, plotId])
  
  const handleViewAllTasks = useCallback(() => {
    onNavigate('tasks', { filterByPlotId: plotId })
  }, [onNavigate, plotId])
  
  // AGC: [FIX] State for fullscreen map modal
  const [showFullscreenMap, setShowFullscreenMap] = useState(false)
  // AGC: [FIX] Loading state for satellite data fetch
  const [isFetchingSatelliteData, setIsFetchingSatelliteData] = useState(false)
  
  // AGC: [FIX] Auto-fetch satellite data when fullscreen map opens
  useEffect(() => {
    const fetchSatelliteData = async () => {
      if (!showFullscreenMap || !plot?.coordinates || plot.coordinates.length === 0) return
      
      // AGC: Check if we already have data
      const hasNdvi = ndviOverlayUrl || ndviOverlay?.url
      const hasZones = (zonesForMap && zonesForMap.length > 0) || (monitoringZones && monitoringZones.length > 0)
      
      if (hasNdvi && hasZones) return // AGC: Already have data
      
      setIsFetchingSatelliteData(true)
      console.log('🛰️ AGC: Auto-fetching satellite data for fullscreen map...')
      
      try {
        // AGC: Fetch NDVI overlay if not loaded
        if (!hasNdvi) {
          setNdviError(false)
          const url = await fetchNdviOverlay()
          if (url) {
            setNdviOverlayUrl(url)
            setNdviError(false)
            console.log('✅ AGC: NDVI overlay loaded:', url)
          } else {
            setNdviError(true)
            console.warn('⚠️ AGC: NDVI overlay failed - backend may be unreachable or Sentinel Hub credentials invalid')
          }
        }
        
        // AGC: Fetch zones if not loaded
        if (!hasZones) {
          const zones = await fetchZones(3, false)
          if (zones && zones.length > 0) {
            setZonesForMap(zones)
            console.log('✅ AGC: Zones loaded:', zones.length, 'zones')
          }
        }
      } catch (error) {
        console.error('❌ AGC: Error fetching satellite data:', error)
      } finally {
        setIsFetchingSatelliteData(false)
      }
    }
    
    fetchSatelliteData()
  }, [showFullscreenMap, plot?.coordinates, ndviOverlayUrl, zonesForMap, ndviOverlay, monitoringZones, fetchNdviOverlay, fetchZones])
  
  // AGC: [FIX] Combined NDVI URL from hook or manual state
  const effectiveNdviUrl = useMemo(() => {
    return ndviOverlayUrl || ndviOverlay?.url || null
  }, [ndviOverlayUrl, ndviOverlay])
  
  // AGC: [FIX] Combined zones from hook or manual state
  const effectiveZones = useMemo(() => {
    if (zonesForMap && zonesForMap.length > 0) return zonesForMap
    if (monitoringZones && monitoringZones.length > 0) return monitoringZones
    return null
  }, [zonesForMap, monitoringZones])
  
  // AGC: [FIX] Map press now shows fullscreen map modal, NOT deprecated plot-detail page
  const handleMapPress = useCallback(() => {
    setShowFullscreenMap(true)
  }, [])
  
  // AGC: Handle archive lot
  const handleArchiveLot = useCallback(async () => {
    if (!plot) return
    
    setIsArchiving(true)
    try {
      await archivePlot(plotId, plot.fieldId)
      console.log('✅ AGC: Lot archived successfully:', plotId)
      setShowArchiveDialog(false)
      // AGC: Navigate back to lot list after archiving
      onBack()
    } catch (error) {
      console.error('❌ AGC: Error archiving lot:', error)
      alert(t('lotPage.archiveError'))
    } finally {
      setIsArchiving(false)
    }
  }, [plot, plotId, archivePlot, onBack, t])
  
  // AGC: [NOTES] Handle photo capture
  const handlePhotoCapture = useCallback(async (photo: Blob, gpsLocation?: GPSLocation, description?: string, taggedUserIds?: string[]) => {
    if (!user) {
      console.error('AGC: No user found for creating note')
      return
    }

    setIsSavingNote(true)
    try {
      const request: CreateNoteRequest = {
        type: 'photo' as LotNoteType,
        content: description,
        file: photo,
        lotIds: [plotId],
        taggedUserIds,
        gpsLocation,
      }
      
      await createNote(
        request,
        user.uid,
        user.displayName || user.email || t('common.unknown'),
        user.email || ''
      )

      console.log('✅ AGC: Photo note saved successfully')
      toast.success(t('notes.photoSaved'))
      setNotesRefreshCounter(c => c + 1)
    } catch (error: any) {
      console.error('❌ AGC: Error saving photo note:', error?.message || error)
      toast.error(t('notes.saveError', { type: t('notes.photo'), error: error?.message || '' }))
    } finally {
      setIsSavingNote(false)
    }
  }, [plotId, user])

  // AGC: [NOTES] Handle video capture
  const handleVideoCapture = useCallback(async (video: Blob, gpsLocation?: GPSLocation, description?: string, taggedUserIds?: string[]) => {
    if (!user) {
      console.error('AGC: No user found for creating note')
      return
    }

    setIsSavingNote(true)
    try {
      const request: CreateNoteRequest = {
        type: 'video' as LotNoteType,
        content: description,
        file: video,
        lotIds: [plotId],
        taggedUserIds,
        gpsLocation,
      }

      await createNote(
        request,
        user.uid,
        user.displayName || user.email || t('common.unknown'),
        user.email || ''
      )

      console.log('✅ AGC: Video note saved successfully')
      toast.success(t('notes.videoSaved'))
      setNotesRefreshCounter(c => c + 1)
    } catch (error: any) {
      console.error('❌ AGC: Error saving video note:', error?.message || error)
      toast.error(t('notes.saveError', { type: t('notes.video'), error: error?.message || '' }))
    } finally {
      setIsSavingNote(false)
    }
  }, [plotId, user])

  // AGC: [NOTES] Handle audio note save
  const handleAudioSave = useCallback(async (audio: Blob, duration: number, gpsLocation?: GPSLocation, description?: string, taggedUserIds?: string[]) => {
    if (!user) {
      console.error('AGC: No user found for creating note')
      return
    }

    setIsSavingNote(true)
    try {
      const request: CreateNoteRequest = {
        type: 'audio' as LotNoteType,
        content: description,
        file: audio,
        lotIds: [plotId],
        taggedUserIds,
        gpsLocation,
      }
      
      await createNote(
        request,
        user.uid,
        user.displayName || user.email || t('common.unknown'),
        user.email || ''
      )

      console.log('✅ AGC: Audio note saved successfully')
      toast.success(t('notes.audioSaved'))
      setNotesRefreshCounter(c => c + 1)
    } catch (error: any) {
      console.error('❌ AGC: Error saving audio note:', error?.message || error)
      toast.error(t('notes.saveError', { type: t('notes.audioNote'), error: error?.message || '' }))
    } finally {
      setIsSavingNote(false)
    }
  }, [plotId, user])
  
  // AGC: [NOTES] Handle text note save (with optional media attachment)
  const handleTextSave = useCallback(async (
    content: string,
    lotIds: string[],
    taggedUserIds: string[],
    gpsLocation?: GPSLocation,
    attachment?: { file: Blob; type: 'photo' | 'video' | 'audio' }
  ) => {
    if (!user) {
      console.error('AGC: No user found for creating note')
      return
    }

    setIsSavingNote(true)
    try {
      const request: CreateNoteRequest = {
        type: 'text' as LotNoteType,
        content,
        file: attachment?.file,
        attachmentType: attachment?.type,
        lotIds,
        taggedUserIds,
        gpsLocation,
      }
      
      await createNote(
        request,
        user.uid,
        user.displayName || user.email || t('common.unknown'),
        user.email || ''
      )

      console.log('✅ AGC: Text note saved successfully')
      setNotesRefreshCounter(c => c + 1)
    } catch (error) {
      console.error('❌ AGC: Error saving text note:', error)
    } finally {
      setIsSavingNote(false)
    }
  }, [user])
  
  // AGC: Loading state
  if (!plot) {
    return (
      <div className="min-h-screen bg-[#F4F5F7] dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">{t('lotPage.lotNotFound')}</p>
          <button
            onClick={onBack}
            className="mt-4 px-4 py-2 bg-[#0B5F4A] text-white rounded-lg hover:bg-[#094935] transition-colors"
          >
            {t('common.back')}
          </button>
        </div>
      </div>
    )
  }
  
  const isLoading = weatherLoading && !weatherHealth
  
  return (
    <div className="min-h-screen bg-[#F4F5F7] dark:bg-slate-900 pb-20">
      {/* AGC: Header with QuickAddMenu for notes */}
      {headerData && (
        <LotHeader 
          data={headerData} 
          onBack={onBack}
          noteCount={noteCount}
          onPhotoCapture={() => setShowPhotoModal(true)}
          onVideoCapture={() => setShowVideoModal(true)}
          onAudioNote={() => setShowAudioModal(true)}
          onTextNote={() => setShowTextModal(true)}
          onTimelineClick={() => setShowTimelineOverlay(true)}
        />
      )}
      
      {/* AGC: Loading indicator for initial load */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-[#0B5F4A] animate-spin" />
        </div>
      )}
      
      {/* AGC: Error state */}
      {weatherError && !weatherHealth && (
        <div className="mx-4 mt-4 p-4 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800">
          <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
            <AlertTriangle className="w-5 h-5" />
            <span>{t('lotPage.weatherError')}</span>
          </div>
          <button
            onClick={refetchWeather}
            className="mt-2 flex items-center gap-1 text-sm text-red-600 dark:text-red-400 hover:underline"
          >
            <RefreshCw className="w-4 h-4" />
            {t('common.refresh')}
          </button>
        </div>
      )}
      
      {/* AGC: Main content */}
      {!isLoading && (
        <div className="px-4 py-4 space-y-4">
          {/* AGC: Weather Health Card */}
          {weatherHealth && (
            <WeatherHealthCard
              weatherHealth={weatherHealth}
              onViewDetails={handleViewWeatherDetails}
              safraStartDate={plot?.safraStartDate}
              onStartSafra={handleStartSafra}
              onStopSafra={handleStopSafra}
            />
          )}
          
          {/* AGC: Field Monitoring Card (Satellite Health) */}
          {plot?.coordinates && plot.coordinates.length > 0 && (
            <FieldMonitoringCard
              fieldId={plotId}
              fieldPolygon={plot.coordinates as [number, number][]}
              bounds={mapData?.bounds ? {
                northEast: mapData.bounds.northEast,
                southWest: mapData.bounds.southWest,
              } : undefined}
              onViewNdviOverlay={(url) => {
                console.log('AGC: NDVI overlay URL:', url)
                setNdviOverlayUrl(url)
                setShowNdviOverlay(true)
                // AGC: Scroll to map card
                setTimeout(() => {
                  mapCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
                }, 100)
              }}
              onViewZonesOnMap={(zones) => {
                console.log('AGC: Show zones on map:', zones.length, 'zones')
                setZonesForMap(zones)
                setShowZonesOnMap(true)
                // AGC: Also enable NDVI overlay to show colors on map
                setShowNdviOverlay(true)
                // AGC: Scroll to map card
                setTimeout(() => {
                  mapCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
                }, 100)
              }}
              onStressEventClick={(eventId) => {
                console.log('AGC: Stress event clicked:', eventId)
                // AGC: Could navigate to event detail or show on map
              }}
            />
          )}
          
          {/* AGC: Unified Timeline - tasks, notes, phenological stages */}
          <TimelineUnified
            lotId={plotId}
            plantingDate={plot?.plantingDate}
            isExpanded={showTimeline}
            onToggleExpand={() => setShowTimeline(!showTimeline)}
          />

          {/* AGC: Tasks Card */}
          <LotTasksCard
            lotId={plotId}
            previewTasks={lotTasks.slice(0, 5)}
            allTasks={lotTasks}
            totalTasksCount={totalTasksCount}
            activeFilters={taskFilters}
            onFilterChange={setTaskFilters}
            onTaskPress={handleTaskPress}
            onAddTask={handleAddTask}
            onViewAll={handleViewAllTasks}
          />
          
          {/* AGC: [INVENTORY] Bill of Materials (BOM) Card - Shows required inputs */}
          {plot && (
            <LotBOMCard
              lotId={plotId}
              lotName={plot.name}
              areaHa={plot.area}
              cropId={plot.cropType || ''}
              cropName={plot.cropName || plot.cropType || ''}
              farmName={currentFarm?.name || t('lotPage.defaultFarmName')}
              farmLocation={currentFarm?.location ? { lat: currentFarm.location.lat, lng: currentFarm.location.lng } : undefined}
              allLots={plots.filter(p => p.id !== plotId && p.cropType).map(p => ({
                id: p.id,
                name: p.name,
                cropName: p.cropName || p.cropType || '',
              }))}
              onNavigateToInventory={() => onNavigate?.('inventory')}
            />
          )}
          
          {/* AGC: [NOTES] Timeline removed from inline view - now only accessible via PlotTimelineOverlay */}
          {/* AGC: User can access timeline via "View timeline" action in LotHeader */}
          
          {/* AGC: Map Preview Card with NDVI overlay and zones support */}
          <div ref={mapCardRef}>
            {mapData && (
              <LotMapPreviewCard
                mapData={mapData}
                onPress={handleMapPress}
                ndviOverlayUrl={effectiveNdviUrl}
                showNdviOverlay={showNdviOverlay}
                onToggleNdviOverlay={() => setShowNdviOverlay(!showNdviOverlay)}
                zones={effectiveZones}
                showZones={showZonesOnMap}
                onToggleZones={() => setShowZonesOnMap(!showZonesOnMap)}
              />
            )}
          </div>
          
          {/* AGC: [NOTES] Lot Notes Card — between map and audit */}
          <div className="mt-4">
            <LotNotesCard
              lotId={plotId}
              onNoteClick={(note) => setSelectedNote(note)}
              refreshTrigger={notesRefreshCounter}
            />
          </div>

          {/* AGC: [AUDIT] Audit Log Card */}
          {fieldId && (
            <div className="mt-4">
              <LotAuditLogCard
                lotId={plotId}
                lotName={plot?.name || ''}
                fieldId={fieldId}
                onViewLog={() => {
                  console.log('🔍 AGC: [LOT_PAGE] onViewLog callback triggered, setting showAuditLogViewer=true')
                  setShowAuditLogViewer(true)
                }}
              />
            </div>
          )}
          
          {/* AGC: Archive Lot Button */}
          <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setShowArchiveDialog(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded-xl border border-amber-200 dark:border-amber-800 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
            >
              <Archive className="w-5 h-5" />
              <span>{t('lotPage.archiveLot')}</span>
            </button>
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-2">
              {t('lotPage.archiveDescription')}
            </p>
          </div>
        </div>
      )}
      
      {/* AGC: Archive Confirmation Dialog */}
      {showArchiveDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[90] p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 max-w-sm w-full shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
                  <Archive className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t('lotPage.archiveConfirmTitle')}
                </h3>
              </div>
              <button
                onClick={() => setShowArchiveDialog(false)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
                disabled={isArchiving}
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              {t('lotPage.archiveConfirmMessage')}
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowArchiveDialog(false)}
                disabled={isArchiving}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleArchiveLot}
                disabled={isArchiving}
                className="flex-1 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isArchiving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('common.loading')}
                  </>
                ) : (
                  <>
                    <Archive className="w-4 h-4" />
                    {t('lotPage.archiveConfirmButton')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* AGC: [AUDIT] Audit Log Viewer Modal */}
      {fieldId && (
        <>
          {showAuditLogViewer && console.log('🔍 AGC: [LOT_PAGE] Rendering LotAuditLogViewer, isOpen:', showAuditLogViewer)}
          <LotAuditLogViewer
            lotId={plotId}
            lotName={plot?.name || ''}
            fieldId={fieldId}
            isOpen={showAuditLogViewer}
            onClose={() => {
              console.log('🔍 AGC: [LOT_PAGE] Closing audit log viewer')
              setShowAuditLogViewer(false)
            }}
          />
        </>
      )}
      
      {/* AGC: [FIX] Fullscreen Map Modal */}
      {showFullscreenMap && mapData && (
        <div className="fixed inset-0 bg-black/90 z-[90] flex flex-col">
          {/* AGC: Map header with back button - visible on Android */}
          <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/80 via-black/50 to-transparent px-3 pb-6" style={{ paddingTop: 'max(env(safe-area-inset-top, 8px), 8px)' }}>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFullscreenMap(false)}
                className="p-2.5 bg-white/30 hover:bg-white/40 rounded-full transition-colors backdrop-blur-sm"
              >
                <ArrowLeft className="w-5 h-5 text-white" />
              </button>
              <div className="flex-1 min-w-0">
                <h2 className="text-white text-base font-bold truncate">{plot?.name || t('lotPage.mapTitle')}</h2>
                <p className="text-white/80 text-xs">{t('fields.area')}: {plot?.area?.toFixed(1) || 0} ha</p>
              </div>
              <button
                onClick={() => setShowFullscreenMap(false)}
                className="p-2.5 bg-white/30 hover:bg-white/40 rounded-full transition-colors backdrop-blur-sm"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>
          
          {/* AGC: Fullscreen map */}
          <div className="flex-1">
            <LotMapPreviewCard
              mapData={{
                ...mapData,
                polygon: mapData.polygon,
              }}
              onPress={() => setShowFullscreenMap(false)}
              ndviOverlayUrl={effectiveNdviUrl}
              showNdviOverlay={showNdviOverlay}
              onToggleNdviOverlay={() => setShowNdviOverlay(!showNdviOverlay)}
              zones={effectiveZones}
              showZones={showZonesOnMap}
              onToggleZones={() => setShowZonesOnMap(!showZonesOnMap)}
              isFullscreen={true}
              isLoadingLayers={isFetchingSatelliteData || monitoringLoading}
              ndviError={ndviError}
            />
          </div>
        </div>
      )}
      
      {/* AGC: [NOTES] Photo Capture Modal */}
      <PhotoCaptureModal
        isOpen={showPhotoModal}
        onClose={() => setShowPhotoModal(false)}
        onCapture={handlePhotoCapture}
        lotId={plotId}
      />
      
      {/* AGC: [NOTES] Video Capture Modal */}
      <VideoCaptureModal
        isOpen={showVideoModal}
        onClose={() => setShowVideoModal(false)}
        onCapture={handleVideoCapture}
        lotId={plotId}
      />

      {/* AGC: [NOTES] Audio Note Modal */}
      <AudioNoteModal
        isOpen={showAudioModal}
        onClose={() => setShowAudioModal(false)}
        onSave={handleAudioSave}
        lotId={plotId}
      />
      
      {/* AGC: [NOTES] Text Note Modal */}
      <TextNoteModal
        isOpen={showTextModal}
        onClose={() => setShowTextModal(false)}
        onSave={handleTextSave}
        defaultLotId={plotId}
      />

      {/* AGC: [NOTES] Note Detail View */}
      <NoteDetailView
        note={selectedNote}
        isOpen={!!selectedNote}
        onClose={() => setSelectedNote(null)}
      />

      {/* AGC: Bottom Navigation */}
      <BottomNav 
        active="fields" 
        onNavigate={(tab) => {
          if (tab === 'dashboard') onNavigate('dashboard', {}) // AGC: Clear context when going to dashboard
          else if (tab === 'fields') onNavigate('lotes-list', {}) // AGC: Navigate to lotes-list (not 'fields')
          else if (tab === 'tasks') onNavigate('tasks', {})
          else if (tab === 'weather') onNavigate('weather', {})
        }} 
      />
      
      {/* AGC: [PLOT_TIMELINE] Timeline Overlay - Full-screen modal */}
      {showTimelineOverlay && plot && (
        <PlotTimelineOverlay
          plotId={plotId}
          plotName={plot.name}
          onClose={() => setShowTimelineOverlay(false)}
          onNavigate={onNavigate}
        />
      )}
    </div>
  )
}

export default LotPage

